import { lazy, Suspense } from 'react';
import { PermissionGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import DashboardLayout from 'src/layouts/dashboard';
import { ROLES, PERMISSIONS } from 'src/api/roles-config';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import DistributorGuard from 'src/components/auth/guard/distributor-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { DistributorsListSerachParamsSchema } from 'src/sections/distributor/distributor-list/search-params';
import { DistributorDetailsSearchParamsSchema } from 'src/sections/distributor/distributor-details/search-params';
import { DistributorSiteDetailsSearchParamsSchema } from 'src/sections/distributor/distributor-site-details/search-params';

import { rootRoute } from './root';

const AddDistributorOrganization = lazy(
  () => import('src/sections/distributor/add-distributor/view')
);
const DistributorOrgDetails = lazy(
  () => import('src/sections/distributor/distributor-details/view')
);
const DistributorOrgList = lazy(() => import('src/sections/distributor/distributor-list/view'));
const DistributorSiteDetails = lazy(
  () => import('src/sections/distributor/distributor-site-details/view')
);
const BulkUploadProductPrices = lazy(
  () => import('src/sections/products-bulk-actions/prices/bulk-upload-product-prices')
);
const BulkUploadProductTemplates = lazy(
  () => import('src/sections/products-bulk-actions/templates/bulk-upload-product-templates')
);
const BulkUploadBespokePrices = lazy(
  () => import('src/sections/products-bulk-actions/bespoke-prices/bulk-upload-bespoke-prices')
);

// Distributors routes
const distributorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'suppliers',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AppAuthGuard>
  ),
});

const distributorsIndexRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    DistributorsListSerachParamsSchema.validateSync(search),
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.DISTRIBUTORS_LIST_ADMIN]}>
      <DistributorOrgList />
    </PermissionGuard>
  ),
});

const addDistributorRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: 'add',
  component: () => (
    <PermissionGuard requiredPermissions={[PERMISSIONS.DISTRIBUTORS_CREATE]}>
      <AddDistributorOrganization />
    </PermissionGuard>
  ),
});

const distributorDetailsRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '$supplierOrgId',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    DistributorDetailsSearchParamsSchema.validateSync(search),
  component: () => {
    const { supplierOrgId: distributorOrgId } = distributorDetailsRoute.useParams();
    return (
      <DistributorGuard
        orgId={distributorOrgId}
        requiredRoles={[
          ROLES.ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.PRODUCER_ADMIN,
          ROLES.ENTEGRA_USER,
          ROLES.ENTEGRA_PRICE_ADMIN,
          ROLES.DATA_MANAGER,
        ]}
      >
        <DistributorOrgDetails />
      </DistributorGuard>
    );
  },
});

const distributorBulkProductTemplatesRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '$supplierOrgId/bulk-product-templates',
  component: () => {
    const { supplierOrgId: distributorOrgId } = distributorBulkProductTemplatesRoute.useParams();
    return (
      <DistributorGuard
        orgId={distributorOrgId}
        requiredRoles={[ROLES.ADMIN, ROLES.DISTRIBUTOR_ADMIN, ROLES.DATA_MANAGER]}
      >
        <BulkUploadProductTemplates />
      </DistributorGuard>
    );
  },
});

const distributorBulkProductPricesRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '$supplierOrgId/bulk-product-prices',
  component: () => {
    const { supplierOrgId: distributorOrgId } = distributorBulkProductPricesRoute.useParams();
    return (
      <DistributorGuard orgId={distributorOrgId} requiredRoles={[ROLES.ADMIN, ROLES.DATA_MANAGER]}>
        <BulkUploadProductPrices />
      </DistributorGuard>
    );
  },
});

const distributorBulkBespokePricesRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '$supplierOrgId/bulk-bespoke-prices',
  component: () => {
    const { supplierOrgId: distributorOrgId } = distributorBulkBespokePricesRoute.useParams();
    return (
      <DistributorGuard
        orgId={distributorOrgId}
        requiredRoles={[ROLES.ADMIN, ROLES.ENTEGRA_USER, ROLES.ENTEGRA_PRICE_ADMIN]}
      >
        <BulkUploadBespokePrices />
      </DistributorGuard>
    );
  },
});

const distributorSiteDetailsRoute = createRoute({
  getParentRoute: () => distributorsRoute,
  path: '$supplierOrgId/sites/$siteId',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    DistributorSiteDetailsSearchParamsSchema.validateSync(search),
  component: () => {
    const { supplierOrgId: distributorOrgId, siteId } = distributorSiteDetailsRoute.useParams();
    return (
      <DistributorGuard
        orgId={distributorOrgId}
        siteId={siteId}
        requiredRoles={[
          ROLES.ADMIN,
          ROLES.DISTRIBUTOR_ADMIN,
          ROLES.ENTEGRA_USER,
          ROLES.ENTEGRA_PRICE_ADMIN,
          ROLES.DATA_MANAGER,
        ]}
      >
        <DistributorSiteDetails />
      </DistributorGuard>
    );
  },
});

export const supplierRouteTree = distributorsRoute.addChildren([
  distributorsIndexRoute,
  addDistributorRoute,
  distributorDetailsRoute,
  distributorBulkProductTemplatesRoute,
  distributorBulkProductPricesRoute,
  distributorBulkBespokePricesRoute,
  distributorSiteDetailsRoute,
]);
